import { useNavigate } from 'react-router-dom'
import { RankedBar, SAMPLE_FLOOR } from '../charts.jsx'
import { Card, ErrorState, Loading, Metric, MetricRow, SectionHeader, StatusPill, Table, fmtMinutes, fmtPct } from '../components.jsx'
import { useApi, useRange } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * Per-crane reliability: the MTBF table that previously existed only inside a downloadable spreadsheet.
 *
 * MTBF was computed correctly all along (`ReportService.kt:358`) and a supervisor could reach it only by
 * exporting XLSX. That is the precise shape of the "documentation dashboard" problem — the plant's central
 * reliability number was operational on the server and clerical in practice.
 *
 * **Cranes with no failures appear here.** Iterating the fleet rather than grouping complaints was audit
 * defect 5 on the reliability report: grouping omits every crane with a clean sheet, so the most reliable
 * machines vanish from the reliability view and a reader cannot tell "no failures" from "not covered". Their
 * MTBF shows an em dash, never 0 — reporting zero would rank the best machine in the plant as the worst.
 */
export default function Reliability() {
  const nav = useNavigate()
  const { key, setKey, department, setDepartment, query } = useRange('90')

  const rows = useApi(`/rcm/asset-reliability${query}`)
  const pmEff = useApi('/rcm/pm-effectiveness?windowDays=30')
  const components = useApi(`/rcm/component-reliability${query}`)

  const data = rows.data || []
  const withFailures = data.filter((r) => r.failures > 0)
  const clean = data.length - withFailures.length

  return (
    <>
      <PageHeader
        title="Reliability"
        subtitle="Per crane, per component, and whether preventive maintenance is working"
        range={{ key, setKey, department, setDepartment }}
      />

      {rows.loading && <Loading rows={6} />}
      {rows.error && <ErrorState error={rows.error} onRetry={rows.reload} />}

      {rows.data && (
        <>
          <MetricRow>
            <Metric label="Cranes covered" value={data.length} hint={`${clean} with no failures`} />
            <Metric
              label="Best MTBF"
              value={fmtMinutes(bestMtbf(withFailures))}
              hint={bestName(withFailures)}
              kind="success"
            />
            <Metric
              label="Worst MTBF"
              value={fmtMinutes(worstMtbf(withFailures))}
              hint={worstName(withFailures)}
              kind={withFailures.length ? 'danger' : undefined}
            />
            <Metric
              label="Repeat failures"
              value={data.reduce((a, r) => a + r.repeatFailures, 0)}
              hint="Same confirmed cause, twice"
              kind={data.some((r) => r.repeatFailures > 0) ? 'warning' : 'success'}
            />
          </MetricRow>

          <SectionHeader hint="Sorted by downtime. A dash means no failures, not zero reliability.">
            Crane reliability
          </SectionHeader>
          <Card>
            <Table
              keyOf={(r) => r.assetId}
              onRowClick={(r) => nav(`/cranes/${r.assetId}`)}
              columns={[
                {
                  key: 'assetCode',
                  header: 'Crane',
                  render: (r) => (
                    <span className="cell-stack">
                      <strong>{r.assetCode}</strong>
                      <span className="muted">{r.assetName}</span>
                    </span>
                  ),
                },
                { key: 'department', header: 'Department', render: (r) => `${r.department} · ${r.area}` },
                {
                  key: 'state',
                  header: 'State',
                  render: (r) => (r.downNow ? <StatusPill status="UNDER_MAINTENANCE" /> : <StatusPill status="ACTIVE" />),
                },
                { key: 'complaints', header: 'Complaints', numeric: true },
                { key: 'failures', header: 'Failures', numeric: true },
                {
                  key: 'repeat',
                  header: 'Repeats',
                  numeric: true,
                  render: (r) => (r.repeatFailures > 0
                    ? <strong title="Same confirmed failure code recurred — root cause not eliminated">{r.repeatFailures}</strong>
                    : '0'),
                },
                { key: 'mtbf', header: 'MTBF', numeric: true, render: (r) => fmtMinutes(r.mtbfMinutes) ?? <span className="muted">—</span> },
                { key: 'mttr', header: 'MTTR', numeric: true, render: (r) => fmtMinutes(r.mttrMinutes) ?? <span className="muted">—</span> },
                { key: 'mtta', header: 'MTTA', numeric: true, render: (r) => fmtMinutes(r.mttaMinutes) ?? <span className="muted">—</span> },
                { key: 'downtime', header: 'Downtime', numeric: true, render: (r) => fmtMinutes(r.downtimeMinutes) },
                {
                  key: 'avail',
                  header: 'Availability',
                  numeric: true,
                  render: (r) => <span className={availClass(r.availabilityPct)}>{fmtPct(r.availabilityPct)}%</span>,
                },
              ]}
              rows={data}
            />
          </Card>
        </>
      )}

      <div className="grid-2" style={{ marginTop: 'var(--lg)' }}>
        <Card
          title="Is preventive maintenance working?"
          subtitle="Failures in the 30 days before a PM, against the 30 days after"
        >
          {pmEff.loading && <Loading rows={3} />}
          {pmEff.error && <ErrorState error={pmEff.error} onRetry={pmEff.reload} />}
          {pmEff.data && (pmEff.data.pmsCompleted === 0
            ? (
              <p className="muted">
                No completed preventive maintenance on record yet, so there is nothing to evaluate. This
                becomes meaningful once PM rounds are being logged.
              </p>
            )
            : (
              <>
                <MetricRow>
                  <Metric label="PMs completed" value={pmEff.data.pmsCompleted} />
                  <Metric label="Failures before" value={pmEff.data.failuresBeforePm} />
                  <Metric label="Failures after" value={pmEff.data.failuresAfterPm} />
                  <Metric
                    label="Ratio"
                    value={pmEff.data.ratio === null ? null : pmEff.data.ratio.toFixed(2)}
                    hint={ratioHint(pmEff.data.ratio)}
                    kind={pmEff.data.ratio === null ? undefined : pmEff.data.ratio < 1 ? 'success' : 'danger'}
                  />
                </MetricRow>
                {pmEff.data.worseAfter.length > 0 && (
                  <p className="muted">
                    Failures rose after maintenance on: <strong>{pmEff.data.worseAfter.join(', ')}</strong>.
                    Worth reviewing the procedure or what was actually done.
                  </p>
                )}
              </>
            ))}
        </Card>

        <Card title="Component reliability" subtitle="Which parts fail across the whole fleet">
          {components.loading && <Loading rows={4} />}
          {components.error && <ErrorState error={components.error} onRetry={components.reload} />}
          {/*
            A ranked bar above the table — DESIGN.md §11.1.

            The question is "which part fails most", which is rank, and a ten-row table makes the reader compare
            numbers down a column to answer it. Horizontal because the labels are words (`WIRE_ROPE`), which
            rotate illegibly under vertical bars.

            Ranked by **failure count**, not downtime: the table beside it already carries downtime and MTTR,
            and a component that fails constantly and is quick to fix is a different problem from one that fails
            rarely and takes a shift — the chart answers the first, the table the second. The table stays,
            because §11.2 says a chart is never the only presentation of a number.
          */}
          {components.data && components.data.length > 0 && (
            <RankedBar
              title="Failures by component"
              hint="Ranked by how often, not how long"
              rows={components.data.map((c) => ({
                label: c.componentType,
                value: c.failures,
                hint: 'Failures',
                // Unclassified is drawn in steel rather than the brand blue, so it reads as "we do not know"
                // rather than as another named component — the same convention as the failure-mode chart.
                token: c.componentType === 'Not classified' ? 'steel' : undefined,
              }))}
              sampleSize={components.data.reduce((a, c) => a + c.failures, 0)}
              sampleNote={componentNote(components.data)}
              empty="No classified failures in this period"
            />
          )}

          {components.data && (
            <Table
              keyOf={(r) => r.componentType}
              columns={[
                { key: 'componentType', header: 'Component' },
                { key: 'failures', header: 'Failures', numeric: true },
                { key: 'assets', header: 'Cranes', numeric: true, render: (r) => r.affectedAssets },
                { key: 'downtime', header: 'Downtime', numeric: true, render: (r) => fmtMinutes(r.downtimeMinutes) },
                { key: 'mttr', header: 'MTTR', numeric: true, render: (r) => fmtMinutes(r.mttrMinutes) ?? '—' },
              ]}
              rows={components.data}
              empty={<p className="muted">No classified failures in this period.</p>}
            />
          )}
          {components.data?.some((c) => c.componentType === 'Not classified') && (
            <p className="muted">
              &quot;Not classified&quot; is downtime where no failure code was confirmed on resolution — it is
              shown rather than hidden, because unclassified downtime is itself the finding.
            </p>
          )}
        </Card>
      </div>
    </>
  )
}

function availClass(pct) {
  return pct >= 95 ? 'val-good' : pct >= 85 ? 'val-warn' : 'val-bad'
}

const mtbfs = (rows) => rows.map((r) => r.mtbfMinutes).filter((v) => v !== null && v !== undefined)
const bestMtbf = (rows) => (mtbfs(rows).length ? Math.max(...mtbfs(rows)) : null)
const worstMtbf = (rows) => (mtbfs(rows).length ? Math.min(...mtbfs(rows)) : null)
const bestName = (rows) => rows.find((r) => r.mtbfMinutes === bestMtbf(rows))?.assetCode || ''
const worstName = (rows) => rows.find((r) => r.mtbfMinutes === worstMtbf(rows))?.assetCode || ''

function ratioHint(ratio) {
  if (ratio === null) return 'No failures before, nothing to compare'
  return ratio < 1 ? 'Fewer failures after — PM is earning its downtime' : 'No improvement after maintenance'
}

/**
 * The sample size, plus the one caveat this particular chart needs.
 *
 * Unclassified failures are counted in the bars because unclassified downtime is itself the finding — but a
 * chart where the tallest bar is "we did not record a cause" needs to say so, or it reads as a component
 * called "Not classified" being the fleet's worst part.
 */
function componentNote(rows) {
  const total = rows.reduce((a, c) => a + c.failures, 0)
  if (!total) return undefined
  const parts = [
    total < SAMPLE_FLOOR
      ? `Based on ${total} failure${total === 1 ? '' : 's'} — too few to read as a trend`
      : `Based on ${total} failures`,
  ]
  const unknown = rows.find((c) => c.componentType === 'Not classified')?.failures || 0
  if (unknown > 0) parts.push(`${Math.round((unknown / total) * 100)}% with no confirmed failure code`)
  return parts.join(' · ')
}
